import { useState, useEffect } from "react";
import { motion } from "framer-motion"
import { Button } from "@components/react/pricing/ui/button";
import { X } from "lucide-react";

interface StickyCTAProps {
  onRequestQuote: () => void;
  onStartTrial: () => void;
  onFindPlan: () => void;
}

export function StickyCTA({ onRequestQuote, onStartTrial, onFindPlan }: StickyCTAProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = document.body.scrollTop;
      const scrollThreshold = document.body.scrollHeight * 0.25;

      if (!isDismissed && scrollTop > scrollThreshold) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    document.body.addEventListener("scroll", handleScroll);
    return () => document.body.removeEventListener("scroll", handleScroll);
  }, [isDismissed]);

  const scrollToPackages = () => {
    const el = document.getElementById("popular-packages");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const ctaVariants = {
    hidden: { y: 100, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", damping: 20, stiffness: 300 },
    },
    exit: { y: 100, opacity: 0, transition: { duration: 0.3, ease: "easeIn" } },
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    const footer = document.querySelector('.pricing-footer');
    if (footer) {
      footer.classList.remove('mb-20', 'sm:mb-28', 'md:mb-20');
    }
  };

  return (
    <>
      {isVisible && !isDismissed && (
        <motion.div
          className="sticky-cta fixed bottom-0 left-0 right-0 z-40 bg-white border-t px-4 py-3 md:py-4"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={ctaVariants}
        >
          <div className="container flex flex-col md:flex-row items-center justify-between">
            <div className="flex-1 mb-3 md:mb-0 text-center md:text-left">
              <h3 className="text-lg font-bold text-gray-900">Ready to secure your data?</h3>
              <p className="text-sm text-gray-600 hidden sm:block">
                Get started with AvePoint's public sector solutions
              </p>
            </div>
            <div className="flex flex-row gap-2">
              <Button
                onClick={onRequestQuote}
                className="bg-[#3037e2] hover:bg-[#2731c8] text-white text-xs md:text-sm"
                size="sm"
              >
                Get Custom Quote
              </Button>
            </div>
            <button
              onClick={handleDismiss}
              className="absolute top-2 right-2 hover:text-gray-900 transition-colors"
              aria-label="Dismiss sticky call to action"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </motion.div>
      )}
    </>
  );
}